export type RenewalVsTurnoverFieldName =
  | 'currentRent'
  | 'renewalRent'
  | 'newTenantRent'
  | 'vacancyMonths'
  | 'cleaningCost'
  | 'additionalCosts';

export type RenewalVsTurnoverValues = Record<RenewalVsTurnoverFieldName, string>;

export type RenewalVsTurnoverErrors = Partial<Record<RenewalVsTurnoverFieldName, string>>;

export type CatchUp = { status: 'future'; month: number } | { status: 'immediate' | 'tied' | 'never' };

export type RentChange = {
  renewal: number;
  newTenant: number;
};

export type RenewalVsTurnoverResult = {
  rentChange: RentChange | undefined;
  cleaningCost: number;
  additionalCosts: number;
  renewalFirstYear: number;
  turnoverFirstYear: number;
  newTenantFullYear: number;
  vacancyLoss: number;
  retentionAdvantage: number;
  monthlyPremium: number;
  upfrontTurnoverLoss: number;
  catchUp: CatchUp;
};

export type RenewalVsTurnoverCalculation =
  | { ok: true; result: RenewalVsTurnoverResult; errors: RenewalVsTurnoverErrors }
  | { ok: false; errors: RenewalVsTurnoverErrors };

const MAX_VACANCY_MONTHS = 12;

export const MAX_INPUT_LENGTH = 15;

const DECIMAL_PATTERN = /^[-+]?(\d+\.?\d*|\.\d+)$/;

const cleanNumber = (value: string) => value.replace(/[,\s$]/g, '');

export const isCurrencyField = (field: RenewalVsTurnoverFieldName) => field !== 'vacancyMonths';

export const parseNumber = (value: string): number | undefined => {
  const cleaned = cleanNumber(value);
  if (cleaned.length > MAX_INPUT_LENGTH || !DECIMAL_PATTERN.test(cleaned)) {
    return undefined;
  }
  return Number(cleaned);
};

export const formatInputValue = (value: string, isCurrency: boolean) => {
  const parsed = parseNumber(value);
  if (parsed === undefined) {
    return value.trim();
  }
  return isCurrency ? parsed.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(parsed);
};

export const formatCurrency = (value: number) =>
  (Math.round(value) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const getFieldError = (field: RenewalVsTurnoverFieldName, value: string, parsed: number | undefined) => {
  if (parsed === undefined) {
    return cleanNumber(value) === '' ? undefined : 'Enter a valid number';
  }
  if (parsed < 0) {
    return 'Enter a number of 0 or more';
  }
  if (field === 'vacancyMonths' && parsed > MAX_VACANCY_MONTHS) {
    return 'Enter 0 to 12 months';
  }
  return undefined;
};

const getCatchUp = (monthlyPremium: number, upfrontTurnoverLoss: number): CatchUp => {
  if (monthlyPremium > 0) {
    return upfrontTurnoverLoss === 0
      ? { status: 'immediate' }
      : { status: 'future', month: Math.ceil(Math.round((upfrontTurnoverLoss / monthlyPremium) * 1e6) / 1e6) };
  }
  return monthlyPremium === 0 && upfrontTurnoverLoss === 0 ? { status: 'tied' } : { status: 'never' };
};

export const calculateRenewalVsTurnover = (values: RenewalVsTurnoverValues): RenewalVsTurnoverCalculation => {
  const errors: RenewalVsTurnoverErrors = {};
  const numbers = {} as Record<RenewalVsTurnoverFieldName, number>;

  (Object.keys(values) as Array<RenewalVsTurnoverFieldName>).forEach((field) => {
    const parsed = parseNumber(values[field]);
    const error = getFieldError(field, values[field], parsed);
    if (error) {
      errors[field] = error;
    }
    numbers[field] = parsed ?? 0;
  });

  const blocksComparison = Object.keys(errors).some((field) => field !== 'currentRent');
  if (blocksComparison) {
    return { ok: false, errors };
  }

  const { renewalRent, newTenantRent, vacancyMonths, cleaningCost, additionalCosts } = numbers;
  const currentRent = errors.currentRent ? undefined : parseNumber(values.currentRent);
  const renewalFirstYear = 12 * renewalRent;
  const turnoverCosts = cleaningCost + additionalCosts;
  const vacancyLoss = vacancyMonths * newTenantRent;
  const turnoverFirstYear = (12 - vacancyMonths) * newTenantRent - turnoverCosts;
  const monthlyPremium = newTenantRent - renewalRent;
  const upfrontTurnoverLoss = vacancyLoss + turnoverCosts;

  return {
    ok: true,
    errors,
    result: {
      rentChange:
        currentRent === undefined
          ? undefined
          : { renewal: renewalRent - currentRent, newTenant: newTenantRent - currentRent },
      cleaningCost,
      additionalCosts,
      renewalFirstYear,
      turnoverFirstYear,
      newTenantFullYear: 12 * newTenantRent,
      vacancyLoss,
      retentionAdvantage: renewalFirstYear - turnoverFirstYear,
      monthlyPremium,
      upfrontTurnoverLoss,
      catchUp: getCatchUp(monthlyPremium, upfrontTurnoverLoss),
    },
  };
};
